import type {
  EAnalyticsScope,
  IDownloadProgressEvent,
  IMessage,
  IParsedProject,
  IProbe,
  IProbeSelectChangeProps,
} from "@frontend-shared/types";
import type { TFunction } from "i18next";

export enum EAnalyticsTab {
  DASHBOARD = "dashboard",
  MESSAGES = "messages",
  PROBES = "probes",
}

// NOTE: Inputs for the Messages-tab default-scope resolver — the value the
// scope dropdown lands on when the user hasn't picked anything yet (initial
// render and after an org switch).
export interface IResolveDefaultScopeProps {
  currentProjectUuid: string | null;
  currentOrganizationUuid: string | null;
  eligibleProjects: IParsedProject[];
  isOwner: boolean;
  // NOTE: Deep-link inputs. A shared message link resolves in the scope that
  // can see it without touching the global project: owners open it in org
  // scope, members in the link's own project.
  hasMessageDeepLink?: boolean;
  deepLinkProjectUuid?: string | null;
}

// NOTE: Inputs for the Messages-tab scope reconciler — given the previously
// selected scope and the latest org/eligibility state, decide whether to keep
// it, fall back to ORG_SCOPE_VALUE, or recompute the default.
export interface IReconcileSelectedScopeProps {
  current: string | null;
  isOwner: boolean;
  eligibleProjects: IParsedProject[];
  computeDefaultScope: () => string | null;
}

export enum EFlagCategory {
  CONTENT_LEAKAGE = "content_leakage",
  POLICY_VIOLATION = "policy_violation",
  JAILBREAK = "jailbreak",
  OFF_TOPIC = "off_topic",
  MISINFORMATION = "misinformation",
  SECURITY_RISK = "security_risk",
}

// NOTE: Option shape for the Messages-tab project/org scope dropdown.
export interface IAnalyticsScopeOption {
  value: string;
  label: string;
  className?: string;
}

export interface IUseProbeBulkSelectionProps {
  probes: IProbe[];
  pageProbes?: IProbe[];
}

// NOTE: Value broadcast by AnalyticsDownloadContext from the Message Details
// modal. When present, leaf file components (MessageFile, ToolFiles, the
// chat-file branch of KnowledgeSources) route their downloads through
// /analytics/message-files instead of the owner-only chat endpoint, which
// is what enables cross-project / org-scope file downloads in Security
// Analytics.
export interface IAnalyticsDownloadContextValue {
  projectUuid: string;
  scope: EAnalyticsScope | null;
}

// NOTE: Inputs for resolveDocumentAnonymizationRequestScope (NSAI-6839).
export interface IResolveDocumentAnonymizationRequestScopeProps {
  analyticsContext: IAnalyticsDownloadContextValue | null;
  projectUuid: string;
}

// NOTE: Inputs for downloadMessageFileWithAnalyticsContext util — the
// dispatcher used by leaf file components to pick between the analytics
// download endpoint and the regular chat one.
export interface IDownloadMessageFileWithAnalyticsContextProps {
  fileUuid: string;
  analyticsContext: IAnalyticsDownloadContextValue | null;
  onProgress?: (event: IDownloadProgressEvent) => void;
}

// NOTE: Inputs for resolveAnalyticsBubbleMessage util — the shared chat-bubble
// mapper used by MessageDetailsModal and ThreadDrawerContent to overlay the
// resolved assistant name (or a deleted-assistant/model marker) onto
// message.model.title.
export interface IResolveAnalyticsBubbleMessageProps {
  message: IMessage;
  t: TFunction<"analytics">;
}

// NOTE: Shape of an option in the member filter dropdown on the Messages tab.
export interface IMemberOption {
  value: string;
  label: string;
}

export interface IUseProbeBulkSelectionReturn {
  selectedUuids: Set<string>;
  handleSelectChange: (props: IProbeSelectChangeProps) => void;
  handleSelectAll: () => void;
  handleSelectAllPages: () => void;
  handleDeselectAll: () => void;
  handleDeselectPage: () => void;
  handleBulkEnable: () => Promise<void>;
  handleBulkDisable: () => Promise<void>;
  handleBulkDelete: () => Promise<void>;
  clearSelection: () => void;
  isAllSelected: boolean;
  isAllPagesSelected: boolean;
  isPageFullySelected: boolean;
  hasMorePagesThanCurrent: boolean;
  pageItemCount: number;
  totalItemCount: number;
  hasSelection: boolean;
  hasPageSelection: boolean;
  selectedCount: number;
  isAllSelectedEnabled: boolean;
  isAllSelectedDisabled: boolean;
  isTogglePending: boolean;
  isDeletePending: boolean;
}
